import { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "../components/atoms/Input/Input";
import { Button } from "../components/atoms/Button/Button";
import { DialogHeader } from "../components/atoms/Dialog/Dialog";
import { PasswordConditionList } from "../features/auth/ui/PasswordFormFields";
import { validatePassword } from "../features/auth/utils/passwordValidation";

/**
 * パスワード再設定ページ（/reset-password でアクセス可能）
 *
 * 画面の流れ:
 * 1. 仮パスワード入力 → 「パスワードの再設定へ」
 * 2. 新しいパスワード入力 + 条件チェック → 「パスワードを再設定する」
 * 3. 再設定完了 → 「ログイン画面へ戻る」
 *
 * 注意:
 * - 現時点では見た目のみの実装。仮パスワード・新しいパスワードの入力はAPIに送信されない
 * - パスワード再設定の仕様が決まり次第、この画面をフローに組み込み、API連携を実装する
 */
const ResetPasswordPage = () => {
    const [temporaryPassword, setTemporaryPassword] = useState("");
    const [showResetForm, setShowResetForm] = useState(false);
    const [isCompleted, setIsCompleted] = useState(false);
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    const navigate = useNavigate();
    const isTemporaryFilled = temporaryPassword.length > 0;

    const handleTemporarySubmit = useCallback((e: React.FormEvent) => {
        e.preventDefault();

        // TODO: 仮パスワード検証APIを呼び出す
        setShowResetForm(true);
    }, []);

    /** すべての条件を満たし、確認パスワードが一致しているか */
    const isResetFormValid = useMemo(() => {
        if (!newPassword || !confirmPassword) return false;
        if (newPassword !== confirmPassword) return false;
        // loginId は仮パスワードリセットのため空文字で渡す（ログインID不明のため）
        return validatePassword(newPassword, "") === null;
    }, [newPassword, confirmPassword]);

    const handleResetSubmit = useCallback((e: React.FormEvent) => {
        e.preventDefault();

        // TODO: パスワード再設定APIを呼び出す
        setIsCompleted(true);
    }, []);

    return (
        <div className="min-h-svh flex items-center justify-center bg-[#EEF6F4]">
            <div className="w-full max-w-lg bg-white rounded-2xl shadow-lg">
                {isCompleted ? (
                    /* ── 再設定完了 ── */
                    <div className="px-8 py-8 space-y-6">
                        <h2 className="text-lg font-semibold text-gray-900">
                            パスワード再設定完了
                        </h2>
                        <p className="text-sm text-gray-700">
                            パスワードの再設定が完了しました。
                        </p>
                        <div className="flex justify-center pt-2">
                            <Button
                                type="button"
                                variant="primary"
                                size="lg"
                                className="w-3/5"
                                onClick={() => navigate("/login")}
                            >
                                ログイン画面へ戻る
                            </Button>
                        </div>
                    </div>
                ) : showResetForm ? (
                    /* ── パスワード再設定フォーム ── */
                    <>
                        <DialogHeader
                            title="パスワードの再設定"
                            className="rounded-t-2xl"
                        />

                        <div className="px-5 py-4">
                            <form onSubmit={handleResetSubmit} className="space-y-5">
                                {/* 新しいパスワード */}
                                <Input
                                    label="新しいパスワード"
                                    placeholder="新しいパスワードを入力"
                                    isPassword
                                    showPasswordToggle
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    autoComplete="new-password"
                                />

                                {/* パスワード条件チェック */}
                                <PasswordConditionList password={newPassword} loginId="" />

                                {/* 新しいパスワードの確認 */}
                                <Input
                                    label="新しいパスワードの確認"
                                    placeholder="もう一度入力してください"
                                    isPassword
                                    showPasswordToggle
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    autoComplete="new-password"
                                    error={confirmPassword && newPassword !== confirmPassword ? "パスワードが一致しません" : undefined}
                                />

                                {/* 再設定ボタン */}
                                <div className="flex justify-center pt-4 pb-4">
                                    <Button
                                        type="submit"
                                        variant={isResetFormValid ? "primary" : "secondary"}
                                        size="lg"
                                        disabled={!isResetFormValid}
                                        className="w-3/5"
                                    >
                                        パスワードを再設定する
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </>
                ) : (
                    /* ── 仮パスワード入力フォーム ── */
                    <>
                        <DialogHeader
                            title="仮パスワードの入力"
                            className="rounded-t-2xl"
                        />

                        <div className="px-5 py-4">
                            <p className="text-sm text-gray-900 mb-8">
                                メールに記載された仮パスワードを入力し、
                                <br />
                                「パスワードの再設定へ」をクリックしてください。
                            </p>

                            <form onSubmit={handleTemporarySubmit} className="space-y-5">
                                {/* 仮パスワード */}
                                <Input
                                    label="仮パスワード"
                                    placeholder="仮パスワードを入力"
                                    isPassword
                                    showPasswordToggle
                                    value={temporaryPassword}
                                    onChange={(e) => setTemporaryPassword(e.target.value)}
                                />

                                {/* 再設定ボタン */}
                                <div className="flex justify-center pt-4 pb-4">
                                    <Button
                                        type="submit"
                                        variant={isTemporaryFilled ? "primary" : "secondary"}
                                        size="lg"
                                        disabled={!isTemporaryFilled}
                                        className="w-3/5"
                                    >
                                        パスワードの再設定へ
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ResetPasswordPage;
